import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { injectStorageTimezone } from '@twaice-fe/frontend/shared/services';
import { selectors } from '@twaice-fe/frontend/shared/store';
import { getOffsetMs, resolveTimezone, tzEndOfDay, tzStartOfDay } from '@twaice-fe/frontend/shared/utilities';
import { DateHelperByDateFns, NzI18nService } from 'ng-zorro-antd/i18n';
import enGB from 'date-fns/locale/en-GB';
import { combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';

const { authSelectors } = selectors;

@Injectable()
export class TimezoneDateHelperService extends DateHelperByDateFns {
  public storageTimezone$: Observable<string | null>;
  public resolvedTimezone$: Observable<string | null>;

  constructor(
    i18n: NzI18nService,
    private store: Store,
    private destroyRef: DestroyRef
  ) {
    super(i18n);
    i18n.setDateLocale(enGB);

    this.storageTimezone$ = injectStorageTimezone().pipe(takeUntilDestroyed(this.destroyRef));

    this.resolvedTimezone$ = combineLatest([this.storageTimezone$, this.store.select(authSelectors.getUserTimezone)]).pipe(
      map(([storageTimezone, userTimezone]) => resolveTimezone(userTimezone ?? null, storageTimezone)),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  override format(date: Date | null, formatStr: string): string {
    if (!date) return '';
    // Dates are pre-shifted by utcToTzDisplay so that browser-local getters
    // already return the selected-TZ wall-clock values. Use the default
    // date-fns formatter which reads those local getters.
    return super.format(date, formatStr);
  }

  /**
   * Convert a UTC Date to a Date that ng-zorro (browser-local) will display
   * as the correct wall-clock time in the selected TZ.
   *
   * ng-zorro renders dates using browser-local getters, so we shift the UTC
   * instant by (selectedTZ offset − browser offset) so the local getters
   * produce the selected-TZ wall-clock values.
   */
  public utcToTzDisplay(date: Date, timezone: string | null): Date {
    if (!timezone) return this.utcToLocalDisplay(date);
    const tzOffsetMs = getOffsetMs(timezone, date);
    const browserOffsetMs = -date.getTimezoneOffset() * 60_000;
    return new Date(date.getTime() + tzOffsetMs - browserOffsetMs);
  }

  /**
   * Inverse of utcToTzDisplay: take a Date whose browser-local getters
   * represent the selected-TZ wall-clock and convert back to a true UTC instant.
   */
  public tzDisplayToUtc(date: Date, timezone: string | null): Date {
    if (!timezone) return this.localAsUTC(date);
    const browserOffsetMs = -date.getTimezoneOffset() * 60_000;
    // First get the approximate UTC instant
    const approxUtc = new Date(date.getTime() - getOffsetMs(timezone, date) + browserOffsetMs);
    // Refine with the offset at the correct UTC instant
    const tzOffsetMs = getOffsetMs(timezone, approxUtc);
    return new Date(date.getTime() - tzOffsetMs + browserOffsetMs);
  }

  public buildCustomTimeRangeValue(displayStart: Date, displayEnd: Date, timezone: string | null): [Date, Date] {
    const utcStart = this.tzDisplayToUtc(displayStart, timezone);
    const utcEnd = this.tzDisplayToUtc(displayEnd, timezone);
    const now = new Date();

    const start = timezone
      ? tzStartOfDay(utcStart, timezone)
      : new Date(Date.UTC(utcStart.getUTCFullYear(), utcStart.getUTCMonth(), utcStart.getUTCDate(), 0, 0, 0, 0));
    const cappedEnd = timezone
      ? tzEndOfDay(utcEnd, timezone)
      : new Date(Date.UTC(utcEnd.getUTCFullYear(), utcEnd.getUTCMonth(), utcEnd.getUTCDate(), 23, 59, 59, 999));

    return [start, cappedEnd > now ? now : cappedEnd];
  }

  private utcToLocalDisplay(date: Date): Date {
    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds()
    );
  }

  private localAsUTC(date: Date): Date {
    return new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
      )
    );
  }
}
